import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism-dark.css';
import {
  FieldPickerPanel,
  buildSuggestions,
  getPartialExpression,
  findExpressionStart,
} from './ExpressionInput';
import { resolveTemplatePreview } from './helpers';

const toJsonString = (v) => {
  if (typeof v === 'string') return v;
  if (v == null) return '';
  try { return JSON.stringify(v, null, 2); } catch { return ''; }
};

const parseObjectEntries = (raw) => {
  try {
    const parsed = JSON.parse(raw || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
    return Object.entries(parsed).map(([key, value]) => {
      if (typeof value === 'number') return { key, type: 'number', value: String(value) };
      if (typeof value === 'boolean') return { key, type: 'boolean', value: String(value) };
      if (typeof value === 'object' && value !== null) return { key, type: 'json', value: JSON.stringify(value, null, 2) };
      return { key, type: 'text', value: value == null ? '' : String(value) };
    });
  } catch {
    return [];
  }
};

const entriesToObject = (entries) => {
  const result = {};
  entries.forEach((entry) => {
    const key = String(entry.key || '').trim();
    if (!key) return;
    if (entry.type === 'number') {
      const num = Number(entry.value);
      result[key] = Number.isNaN(num) ? entry.value : num;
      return;
    }
    if (entry.type === 'boolean') {
      result[key] = entry.value === 'true';
      return;
    }
    if (entry.type === 'json') {
      try {
        result[key] = JSON.parse(entry.value || 'null');
      } catch {
        result[key] = entry.value;
      }
      return;
    }
    result[key] = entry.value;
  });
  return result;
};

const JsonEditor = ({ value, onChange, placeholder, style, readOnly, showAssistTools = true, onDrop, onDragOver, ancestorNodes = [], nodeStatus = {}, envVars = [], ...props }) => {
  const safeValue = toJsonString(value);
  const [showPicker, setShowPicker] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionIdx, setSuggestionIdx] = useState(0);
  const [guidedMode, setGuidedMode] = useState(false);
  const [guidedEntries, setGuidedEntries] = useState([]);
  const [isPoppedOut, setIsPoppedOut] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const getTextarea = () => containerRef.current?.querySelector('textarea') ?? null;

  useEffect(() => {
    if (!dropdownRef.current || suggestions.length === 0) return;
    const items = dropdownRef.current.querySelectorAll('[data-suggestion]');
    items[suggestionIdx]?.scrollIntoView({ block: 'nearest' });
  }, [suggestionIdx, suggestions.length]);

  const highlight = (code) => Prism.highlight(code, Prism.languages.json, 'json');

  const hasExpression = safeValue.includes('{{');
  const canShowAssistTools = !readOnly && showAssistTools;

  const nodeOutputMap = useMemo(() => {
    const map = {};
    for (const n of ancestorNodes) {
      const label = n.data?.label || n.id;
      const out = nodeStatus[n.id]?.output;
      if (out !== undefined) map[label] = out;
    }
    return map;
  }, [ancestorNodes, nodeStatus]);

  const envMap = useMemo(() => {
    const map = {};
    for (const v of envVars) map[v.key] = v.is_encrypted ? '🔒' : v.value;
    return map;
  }, [envVars]);

  const preview = useMemo(() => {
    if (!hasExpression) return null;
    return resolveTemplatePreview(safeValue, { nodes: nodeOutputMap, env: envMap });
  }, [hasExpression, safeValue, nodeOutputMap, envMap]);
  const editorMinHeight = isPoppedOut ? 'clamp(260px, 62vh, 780px)' : 'clamp(120px, 22vh, 260px)';
  const previewMaxHeight = isPoppedOut ? 'clamp(90px, 24vh, 280px)' : 'clamp(60px, 14vh, 140px)';

  useEffect(() => {
    if (!guidedMode) return;
    setGuidedEntries((prev) => {
      if (prev.length > 0) return prev;
      const parsed = parseObjectEntries(safeValue);
      return parsed.length > 0 ? parsed : [{ key: '', type: 'text', value: '' }];
    });
  }, [guidedMode, safeValue]);

  const syncGuidedEntries = useCallback((nextEntries) => {
    setGuidedEntries(nextEntries);
    onChange(JSON.stringify(entriesToObject(nextEntries), null, 2));
  }, [onChange]);

  // ── Autocomplete helpers ──────────────────────────────────────────────────

  const updateSuggestions = useCallback(() => {
    const el = getTextarea();
    if (!el) return;
    const cursor = el.selectionStart;
    const partial = getPartialExpression(el.value, cursor);
    setSuggestions(buildSuggestions(partial, ancestorNodes, nodeStatus, envVars));
    setSuggestionIdx(0);
  }, [ancestorNodes, nodeStatus, envVars]);

  const applySuggestion = useCallback((s) => {
    if (!s.insert) return;
    const el = getTextarea();
    if (!el) return;
    const cursor = el.selectionStart;
    const currentValue = el.value;
    const exprStart = findExpressionStart(currentValue, cursor);
    if (exprStart === -1) return;
    const before = currentValue.slice(0, exprStart);
    const rest = currentValue.slice(cursor).replace(/^\s*\}\}/, '');
    const newVal = `${before}{{ ${s.insert}${rest}`;
    onChange(newVal);
    setSuggestions([]);
    const newCursor = before.length + 3 + s.insert.length;
    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = newCursor;
    }, 0);
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSuggestionIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggestionIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      const s = suggestions[suggestionIdx];
      if (s && !s.isCategory && s.insert) {
        e.preventDefault();
        applySuggestion(s);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
    }
  }, [suggestions, suggestionIdx, applySuggestion]);

  const handleCursorMove = useCallback((e) => {
    if (['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'].includes(e.key)) return;
    updateSuggestions();
  }, [updateSuggestions]);

  // ── Insert from field picker ──────────────────────────────────────────────

  const insertReference = useCallback((ref) => {
    const el = getTextarea();
    const currentValue = el ? el.value : safeValue;
    const cursor = el ? (el.selectionStart ?? currentValue.length) : currentValue.length;
    const newVal = `${currentValue.slice(0, cursor)}${ref}${currentValue.slice(cursor)}`;
    onChange(newVal);
    setShowPicker(false);
    if (el) {
      setTimeout(() => {
        el.focus();
        el.selectionStart = el.selectionEnd = cursor + ref.length;
      }, 0);
    }
  }, [safeValue, onChange]);

  // Close popups on outside click
  useEffect(() => {
    const handler = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowPicker(false);
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsPoppedOut(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!isPoppedOut) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isPoppedOut]);

  return (
    <>
      {isPoppedOut && (
        <div
          onMouseDown={() => setIsPoppedOut(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 1990,
          }}
        />
      )}
      <div
        ref={containerRef}
        style={isPoppedOut ? {
          position: 'fixed',
          top: '5vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(1100px, 96vw)',
          maxHeight: '90vh',
          overflowY: 'auto',
          marginBottom: 0,
          zIndex: 2000,
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '10px',
          boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
          padding: '10px',
        } : { position: 'relative', marginBottom: '15px' }}
      >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
        {canShowAssistTools ? (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
            <button
              type="button"
              onClick={() => {
                const next = !guidedMode;
                setGuidedMode(next);
                if (next) {
                  const parsed = parseObjectEntries(safeValue);
                  setGuidedEntries(parsed.length > 0 ? parsed : [{ key: '', type: 'text', value: '' }]);
                }
              }}
              style={{
                padding: '5px 8px',
                background: guidedMode ? 'var(--accent-color)' : 'var(--button-bg)',
                color: guidedMode ? 'var(--text-inverse)' : 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              Guided
            </button>
            {guidedMode && (
              <button
                type="button"
                onClick={() => syncGuidedEntries([...(guidedEntries || []), { key: '', type: 'text', value: '' }])}
                style={{
                  padding: '5px 8px',
                  background: 'var(--button-bg)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                + Field
              </button>
            )}
          </div>
        ) : <div />}
        <div style={{ display: 'flex', flexShrink: 0, border: '1px solid var(--border-color)', borderRadius: '4px', overflow: 'hidden', alignSelf: 'flex-start' }}>
          <button
            title={isPoppedOut ? 'Collapse editor' : 'Expand editor'}
            onMouseDown={e => e.preventDefault()}
            onClick={() => setIsPoppedOut(v => !v)}
            style={{ padding: '6px 8px', background: isPoppedOut ? 'rgba(99,102,241,0.15)' : 'transparent', border: 'none', borderRight: canShowAssistTools ? '1px solid var(--border-color)' : 'none', cursor: 'pointer', color: isPoppedOut ? 'var(--accent-color)' : 'var(--text-tertiary)', fontSize: '12px', lineHeight: 1, userSelect: 'none' }}
          >{isPoppedOut ? '⤡' : '⤢'}</button>
          {canShowAssistTools && (
            <button
              title="Insert field reference"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { setShowPicker(p => !p); setSuggestions([]); }}
              style={{ padding: '6px 8px', background: showPicker ? 'rgba(99,102,241,0.15)' : 'transparent', border: 'none', cursor: 'pointer', color: showPicker ? 'var(--accent-color)' : 'var(--text-tertiary)', fontSize: '11px', fontFamily: 'monospace', fontWeight: 700, lineHeight: 1, userSelect: 'none' }}
            >{'{}'}</button>
          )}
        </div>
      </div>

      {canShowAssistTools && guidedMode && (
        <div style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px', marginBottom: '8px', display: 'grid', gap: '6px' }}>
          {(guidedEntries || []).map((entry, idx) => (
            <div key={`entry-${idx}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 110px auto', gap: '6px', alignItems: 'center' }}>
              <input
                value={entry.key}
                onChange={(e) => {
                  const next = [...guidedEntries];
                  next[idx] = { ...next[idx], key: e.target.value };
                  syncGuidedEntries(next);
                }}
                style={{ fontSize: '12px', padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                placeholder="Field name"
              />
              <select
                value={entry.type}
                onChange={(e) => {
                  const next = [...guidedEntries];
                  next[idx] = { ...next[idx], type: e.target.value };
                  syncGuidedEntries(next);
                }}
                style={{ fontSize: '12px', padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
              >
                <option value="text">text</option>
                <option value="number">number</option>
                <option value="boolean">boolean</option>
                <option value="json">json</option>
              </select>
              {entry.type === 'boolean' ? (
                <select
                  value={entry.value}
                  onChange={(e) => {
                    const next = [...guidedEntries];
                    next[idx] = { ...next[idx], value: e.target.value };
                    syncGuidedEntries(next);
                  }}
                  style={{ gridColumn: '1 / span 2', fontSize: '12px', padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <input
                  value={entry.value}
                  onChange={(e) => {
                    const next = [...guidedEntries];
                    next[idx] = { ...next[idx], value: e.target.value };
                    syncGuidedEntries(next);
                  }}
                  style={{ gridColumn: '1 / span 2', fontSize: '12px', padding: '6px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                  placeholder={entry.type === 'json' ? '{"nested": "value"}' : 'Value'}
                />
              )}
              <button
                type="button"
                onClick={() => {
                  const next = guidedEntries.filter((_, i) => i !== idx);
                  syncGuidedEntries(next.length > 0 ? next : [{ key: '', type: 'text', value: '' }]);
                }}
                style={{ gridColumn: '3', gridRow: '1 / span 2', alignSelf: 'stretch', padding: '6px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--error-color)', color: 'var(--text-inverse)', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        background: 'var(--bg-input)',
        overflow: 'hidden',
        ...style,
        marginBottom: 0,
      }}>
        <Editor
          value={safeValue}
          onValueChange={onChange}
          highlight={highlight}
          padding={10}
          placeholder={placeholder}
          style={{
            fontFamily: '"Fira code", "Fira Mono", monospace',
            fontSize: 12,
            width: '100%',
            minHeight: editorMinHeight,
            color: 'var(--text-primary)',
            backgroundColor: 'transparent',
          }}
          textareaClassName="json-editor-textarea"
          preClassName="json-editor-pre"
          readOnly={readOnly}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onKeyDown={handleKeyDown}
          onClick={updateSuggestions}
          onKeyUp={handleCursorMove}
          {...props}
        />
      </div>

      {/* Autocomplete dropdown */}
      {canShowAssistTools && suggestions.length > 0 && (
        <div ref={dropdownRef} style={{
          position: 'absolute',
          top: 'calc(100% + 2px)',
          left: 0,
          right: 0,
          background: 'var(--bg-secondary)',
          border: '1px solid var(--accent-color)',
          borderRadius: '4px',
          zIndex: 1100,
          maxHeight: '220px',
          overflowY: 'auto',
          boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
        }}>
          {suggestions.map((s, i) => (
            <div
              key={s.label + i}
              data-suggestion
              onMouseDown={e => { e.preventDefault(); if (!s.isCategory && s.insert) applySuggestion(s); }}
              style={{
                padding: '6px 10px',
                cursor: s.isCategory ? 'default' : 'pointer',
                background: i === suggestionIdx && !s.isCategory ? 'rgba(99,102,241,0.15)' : 'transparent',
                borderBottom: i < suggestions.length - 1 ? '1px solid var(--border-color)' : 'none',
              }}
              onMouseEnter={() => !s.isCategory && setSuggestionIdx(i)}
            >
              <div style={{
                fontFamily: 'monospace',
                fontSize: '12px',
                color: s.isCategory ? 'var(--text-tertiary)' : 'var(--accent-color)',
                fontWeight: s.isCategory ? 400 : 500,
              }}>{s.label}</div>
              {s.desc && (
                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '1px' }}>{s.desc}</div>
              )}
            </div>
          ))}
          <div style={{
            padding: '4px 8px',
            fontSize: '10px',
            color: 'var(--text-tertiary)',
            borderTop: '1px solid var(--border-color)',
            fontStyle: 'italic',
          }}>
            ↑↓ navigate · Enter/Tab insert · Esc dismiss
          </div>
        </div>
      )}

      {/* Unified field picker panel */}
      {canShowAssistTools && showPicker && (
        <FieldPickerPanel
          ancestorNodes={ancestorNodes}
          nodeStatus={nodeStatus}
          envVars={envVars}
          fieldLabel="JSON"
          onInsert={insertReference}
          onClose={() => setShowPicker(false)}
        />
      )}

      {/* Expression preview strip */}
      {preview && !showPicker && suggestions.length === 0 && (
        <div style={{
          marginTop: '3px',
          padding: '4px 8px',
          background: preview.ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
          border: `1px solid ${preview.ok ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
          borderRadius: '4px',
          fontSize: '11px',
          fontFamily: "'Consolas', 'Fira Code', monospace",
          color: preview.ok ? 'var(--text-secondary)' : '#f87171',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '6px',
          wordBreak: 'break-all',
        }}>
          <span style={{ color: preview.ok ? '#4ade80' : '#f87171', flexShrink: 0, marginTop: '1px' }}>
            {preview.ok ? '▶' : '!'}
          </span>
          <span style={{ flex: 1, maxHeight: previewMaxHeight, overflowY: 'auto' }}>
            {preview.resolved === '' ? <em style={{ opacity: 0.5 }}>(empty string)</em> : preview.resolved}
          </span>
        </div>
      )}

      <style>{`
        .json-editor-textarea:focus { outline: none; }
        code[class*="language-"], pre[class*="language-"] { text-shadow: none !important; }
        .token.property { color: #9cdcfe; }
        .token.string { color: #ce9178; }
        .token.number { color: #b5cea8; }
        .token.boolean { color: #569cd6; }
        .token.operator { color: #d4d4d4; }
        .token.punctuation { color: #d4d4d4; }
      `}</style>
      </div>
    </>
  );
};

export default JsonEditor;
